// Enhanced SearchPage.js - Updated with Packages LLM Bridge support

import {
	ArrowRight,
	Brain,
	ChevronDown,
	ChevronUp,
	Globe,
	Layers,
	LogOut,
	Package,
	Search,
	Smartphone,
	Sparkles,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../DomainSearchApp.css";

const SearchPage = ({ user, onLogout }) => {
	const navigate = useNavigate();
	const [searchText, setSearchText] = useState("");
	const [searchType, setSearchType] = useState("domain");
	const [categorySubTab, setCategorySubTab] = useState("domains"); // "domains" | "apps"
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	/**
	 * Safely stores data in sessionStorage by clearing old data first
	 * Prevents QuotaExceededError by ensuring clean storage before writing
	 * @param {string} key - SessionStorage key
	 * @param {object} data - Data to store
	 * @param {string} label - Label for logging
	 * @returns {boolean} - True if stored in sessionStorage, false if fallback used
	 */
	const safelyStoreInSessionStorage = (key, data, label = "data") => {
		try {
			// Step 1: Check data size
			const dataStr = JSON.stringify(data);
			const dataSizeMB = (dataStr.length / 1024 / 1024).toFixed(2);
			console.log(`📊 ${label} size: ${dataSizeMB} MB`);

			// Step 2: Clear ALL sessionStorage to free space
			console.log(
				"🧹 Clearing sessionStorage before storing new LLM results...",
			);
			sessionStorage.clear();
			console.log("✅ SessionStorage cleared successfully");

			// Step 3: Store new data
			sessionStorage.setItem(key, dataStr);
			console.log(`✅ ${label} stored successfully in sessionStorage`);

			return true;
		} catch (storageError) {
			console.error(`❌ SessionStorage failed for ${label}:`, storageError);

			if (storageError.name === "QuotaExceededError") {
				console.warn("⚠️ Storage quota exceeded! Clearing and retrying...");

				// Try clearing and storing again
				try {
					sessionStorage.clear();
					sessionStorage.setItem(key, JSON.stringify(data));
					console.log(`✅ ${label} stored after clearing`);
					return true;
				} catch (retryError) {
					console.error("❌ Still failed after clearing:", retryError);

					// Fallback to window storage
					const fallbackKey = key
						.replace("llm", "temp")
						.replace("SearchResults", "Results");
					window[fallbackKey] = data;
					console.log(`✅ Stored ${label} in window.${fallbackKey} instead`);
					return false;
				}
			}

			// Fallback to window storage for other errors
			const fallbackKey = key
				.replace("llm", "temp")
				.replace("SearchResults", "Results");
			window[fallbackKey] = data;
			console.log(`✅ Stored ${label} in window.${fallbackKey} instead`);
			return false;
		}
	};

	const searchTypes = [
		{ value: "domain", label: "Domains", icon: Globe },
		{ value: "category", label: "Category Search", icon: Layers },
		{ value: "app", label: "App", icon: Smartphone },
		{ value: "packages", label: "Packages", icon: Package },
		{ value: "audience", label: "Audience", icon: Users },
		{ value: "segments", label: "Segments", icon: Target },
	];

	// Enhanced LLM search logic with packages support
	const performLLMSearch = async () => {
		if (!searchText.trim()) {
			setError("Please enter a search term");
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			console.log(
				"🤖 Step 1: Starting LLM search for:",
				searchText,
				"Type:",
				searchType,
			);
			setError(`Step 1: Processing ${searchType} query with AI...`);

			let apiService;
			let searchTypeUpper = searchType.toUpperCase();
			let llmResult;

			// ✅ ADD PACKAGES SEARCH LOGIC
			if (searchType === "packages") {
				// 📦 USE PACKAGES LLM BRIDGE SERVICE
				console.log("📦 Using Packages LLM Bridge Service...");
				setError("Loading Packages AI service...");

				try {
					// Dynamic import with proper error handling
					const { default: PackagesLlmBridgeService } = await import(
						"../services/packagesLlmBridgeService"
					);
					apiService = PackagesLlmBridgeService; // Use the singleton instance
					console.log("✅ Packages LLM Bridge Service loaded:", apiService);

					// Cache the service globally for reuse
					window.packagesLlmBridgeService = apiService;
				} catch (importError) {
					console.error(
						"❌ Failed to import PackagesLlmBridgeService:",
						importError,
					);
					throw new Error(
						"Packages AI service is not available. Please check your configuration.",
					);
				}

				searchTypeUpper = "PACKAGE";

				// ✅ PACKAGES SERVICE HANDLES AUTH INTERNALLY
				console.log(
					"🔒 Packages service will handle authentication internally...",
				);
				setError("Packages AI service is checking authentication...");

				setError("Authentication successful, processing packages with AI...");

				// ✅ CALL PACKAGES LLM API
				console.log("📡 Calling packages LLM API...");
				setError("Step 1: AI is analyzing your packages query...");

				console.log("=== PACKAGES LLM DEBUG ===");

				if (typeof apiService.searchWithNaturalLanguage !== "function") {
					throw new Error(
						"Packages natural language search method is not available in the service.",
					);
				}

				console.log("Calling packages natural language search...");
				llmResult = await apiService.searchWithNaturalLanguage(
					searchText,
					searchTypeUpper,
				);

				// CRITICAL: Debug the actual response structure
				console.log("=== PACKAGES LLM RESULT DEBUG ===");
				console.log("llmResult exists:", !!llmResult);
				console.log("llmResult type:", typeof llmResult);
				console.log(
					"llmResult keys:",
					llmResult ? Object.keys(llmResult) : "null/undefined",
				);
				console.log("llmResult.success:", llmResult?.success);
				console.log("llmResult.data exists:", !!llmResult?.data);
				console.log("llmResult.data type:", typeof llmResult?.data);
				console.log(
					"Full llmResult structure:",
					JSON.stringify(llmResult, null, 2),
				);

				if (!llmResult || !llmResult.success) {
					throw new Error(
						`Packages search failed: ${llmResult?.error || "Unknown error"}`,
					);
				}

				// ✅ NEW: Use the response as-is since LLM bridge service now returns consistent format
				// The updated bridge service returns data in the same format as normal packages API
				console.log(
					"Using LLM result data as-is (consistent with normal packages API)",
				);

				const responseData = {
					success: true,
					totalRecords: llmResult.totalRecords || 0,
					data: llmResult.data, // Use the data structure as returned by the bridge service

					originalData: {
						body: llmResult,
						statusCode: llmResult.statusCode || 200,
						metadata: {
							originalQuery: searchText,
							searchType: searchType,
							isLLMEnhanced: true,
							isPackagesSearch: true,
							llmResponse: llmResult.metadata?.llmPayload,
							searchTime: llmResult.searchTime || 0,
						},
					},
				};

				console.log("PACKAGES LLM SEARCH SUCCESS!", {
					totalRecords: responseData.totalRecords,
					hasData: !!responseData.data,
					dataType: typeof responseData.data,
				});

				// Store results in sessionStorage - use the same structure as normal packages search
				const optimizedData = {
					success: true,
					totalRecords: responseData.totalRecords,
					data: responseData.data, // Keep the original data structure

					appliedFilters: llmResult.appliedFilters || null,

					originalData: {
						metadata: {
							originalQuery: searchText,
							searchType: searchType,
							isLLMEnhanced: true,
							isPackagesSearch: true,
							searchTime: llmResult.searchTime || 0,
							totalRecords: responseData.totalRecords,
							reversedFilters: llmResult.appliedFilters,
							llmPayload:
								llmResult.metadata?.llmPayload ||
								llmResult.originalLlmResponse?.data ||
								null,
						},
					},
				};

				console.log("Storing packages data:", {
					totalRecords: optimizedData.totalRecords,
					hasData: !!optimizedData.data,
					estimatedSize:
						Math.round(JSON.stringify(optimizedData).length / 1024) + " KB",
				});

				try {
					sessionStorage.setItem(
						"llmPackagesSearchResults",
						JSON.stringify(optimizedData),
					);
					console.log("Packages results stored successfully in sessionStorage");
				} catch (storageError) {
					console.error(
						"SessionStorage failed, using memory storage:",
						storageError,
					);
					window.tempPackagesResults = optimizedData;
					console.log("Stored packages results in memory instead");
				}

				setError("Packages LLM search complete! Navigating to results...");
				await new Promise((resolve) => setTimeout(resolve, 500));

				// Navigate to packages interface
				console.log("Navigating to packages results...");
				navigate("/", {
					replace: true,
					state: {
						fromLLMSearch: true,
						fromPackagesLLMSearch: true,
						loadLLMResults: true,
						searchType: "packages",
						activeTab: "packages",
						timestamp: Date.now(),
						useMemoryStorage: !sessionStorage.getItem(
							"llmPackagesSearchResults",
						),
					},
				});

				return; // Exit early for packages since it's a complete flow
			}
			// ✅ CATEGORY SEARCH LOGIC (category-search-text → category-search)
			else if (searchType === "category") {
				console.log("🗂️ Using Category Search Service...");
				setError("Loading Category AI service...");

				let categoryApiService;
				try {
					const { default: CategoryApiService } = await import(
						"../services/CategoryApiService"
					);
					categoryApiService = CategoryApiService;
					window.categoryApiService = categoryApiService;
				} catch (importError) {
					console.error("❌ Failed to import CategoryApiService:", importError);
					throw new Error(
						"Category AI service is not available. Please check your configuration.",
					);
				}

				setError("Step 1: AI is analyzing your category query...");

				const categoryResult =
					await categoryApiService.performCompleteCategorySearch(searchText, categorySubTab);

				if (!categoryResult || !categoryResult.success) {
					throw new Error(
						`Category search failed: ${categoryResult?.error || "Unknown error"}`,
					);
				}

				const optimizedData = {
					success: true,
					...categoryResult.data,
					metadata: {
						originalQuery: searchText,
						searchType: "category",
						isLLMEnhanced: true,
						isCategorySearch: true,
					},
				};

				try {
					sessionStorage.setItem(
						"llmCategorySearchResults",
						JSON.stringify(optimizedData),
					);
				} catch (storageError) {
					console.error(
						"SessionStorage failed, using memory storage:",
						storageError,
					);
					window.tempCategoryResults = optimizedData;
				}

				setError("Category AI search complete! Navigating to results...");
				await new Promise((resolve) => setTimeout(resolve, 500));

				navigate("/", {
					replace: true,
					state: {
						fromLLMSearch: true,
						fromCategoryLLMSearch: true,
						loadLLMResults: true,
						searchType: "category",
						activeTab: "category",
						timestamp: Date.now(),
						useMemoryStorage: !sessionStorage.getItem(
							"llmCategorySearchResults",
						),
					},
				});

				return; // Exit early for category since it's a complete flow
			}
			// ✅ ADD AUDIENCE SEARCH LOGIC
			else if (searchType === "audience") {
				// 🎯 USE AUDIENCE LLM BRIDGE SERVICE
				console.log("👥 Using Audience LLM Bridge Service...");
				setError("Loading Audience AI service...");

				try {
					// Dynamic import with proper error handling
					const { default: LlmApiBridgeService } = await import(
						"../services/LlmApiBridgeService"
					);
					apiService = LlmApiBridgeService; // Use the singleton instance
					console.log("✅ Audience LLM Bridge Service loaded:", apiService);

					// Cache the service globally for reuse
					window.LlmApiBridgeService = apiService;
				} catch (importError) {
					console.error(
						"❌ Failed to import LlmApiBridgeService:",
						importError,
					);
					throw new Error(
						"Audience AI service is not available. Please check your configuration.",
					);
				}

				searchTypeUpper = "AUDIENCE"; // Ensure correct API format
			} else if (searchType === "app") {
				// 📱 USE APP LLM SERVICE - EXISTING LOGIC
				console.log("📱 Using App LLM Service...");
				setError("Loading App AI service...");

				try {
					const { default: AppLLMApiService } = await import(
						"../services/AppLLMApiService"
					);
					apiService = AppLLMApiService;
					console.log("✅ App LLM Service loaded:", apiService);
					window.appLLMApiService = apiService;
				} catch (importError) {
					console.error("❌ Failed to import AppLLMApiService:", importError);
					throw new Error(
						"App AI service is not available. Please check your configuration.",
					);
				}

				searchTypeUpper = "APP";
			} else if (searchType === "segments") {
				// 🧩 USE SEGMENTS LLM BRIDGE SERVICE
				console.log("🧩 Using Segments LLM Bridge Service...");
				setError("Loading Segments AI service...");

				try {
					const { default: SegmentsLlmBridgeService } = await import(
						"../services/segmentsLlmBridgeService"
					);
					apiService = SegmentsLlmBridgeService; // Use the singleton instance
					console.log("✅ Segments LLM Bridge Service loaded:", apiService);

					// Cache the service globally for reuse
					window.segmentsLlmBridgeService = apiService;
				} catch (importError) {
					console.error(
						"❌ Failed to import SegmentsLlmBridgeService:",
						importError,
					);
					throw new Error(
						"Segments AI service is not available. Please check your configuration.",
					);
				}

				searchTypeUpper = "SEGMENTS";

				// ✅ SEGMENTS SERVICE HANDLES AUTH INTERNALLY
				console.log(
					"🔒 Segments service will handle authentication internally...",
				);
				setError("Segments AI service is checking authentication...");

				setError("Authentication successful, processing segments with AI...");

				// ✅ CALL SEGMENTS LLM API
				console.log("📡 Calling segments LLM API...");
				setError("Step 1: AI is analyzing your segments query...");

				console.log("=== SEGMENTS LLM DEBUG ===");

				if (typeof apiService.searchWithNaturalLanguage !== "function") {
					throw new Error(
						"Segments natural language search method is not available in the service.",
					);
				}

				console.log("Calling segments natural language search...");
				llmResult = await apiService.searchWithNaturalLanguage(
					searchText,
					searchTypeUpper,
				);

				// CRITICAL: Debug the actual response structure
				console.log("=== SEGMENTS LLM RESULT DEBUG ===");
				console.log("llmResult exists:", !!llmResult);
				console.log("llmResult type:", typeof llmResult);
				console.log(
					"llmResult keys:",
					llmResult ? Object.keys(llmResult) : "null/undefined",
				);
				console.log("llmResult.success:", llmResult?.success);
				console.log("llmResult.data exists:", !!llmResult?.data);
				console.log("llmResult.data type:", typeof llmResult?.data);
				console.log(
					"Full llmResult structure:",
					JSON.stringify(llmResult, null, 2),
				);

				if (!llmResult || !llmResult.success) {
					throw new Error(
						`Segments search failed: ${llmResult?.error || "Unknown error"}`,
					);
				}

				// ✅ NEW: Use the response as-is since LLM bridge service now returns consistent format
				// The updated bridge service returns data in the same format as normal segments API
				console.log(
					"Using LLM result data as-is (consistent with normal segments API)",
				);

				const responseData = {
					success: true,
					totalRecords: llmResult.totalRecords || 0,
					data: llmResult.data, // Use the data structure as returned by the bridge service

					originalData: {
						body: llmResult,
						statusCode: llmResult.statusCode || 200,
						metadata: {
							originalQuery: searchText,
							searchType: searchType,
							isLLMEnhanced: true,
							isSegmentsSearch: true,
							llmResponse: llmResult.metadata?.llmPayload,
							searchTime: llmResult.searchTime || 0,
						},
					},
				};

				console.log("SEGMENTS LLM SEARCH SUCCESS!", {
					totalRecords: responseData.totalRecords,
					hasData: !!responseData.data,
					dataType: typeof responseData.data,
				});

				// Store results in sessionStorage - use the same structure as normal segments search
				const optimizedData = {
					success: true,
					totalRecords: responseData.totalRecords,
					data: responseData.data, // Keep the original data structure

					appliedFilters: llmResult.appliedFilters || null,

					originalData: {
						metadata: {
							originalQuery: searchText,
							searchType: searchType,
							isLLMEnhanced: true,
							isSegmentsSearch: true,
							searchTime: llmResult.searchTime || 0,
							totalRecords: responseData.totalRecords,
							reversedFilters: llmResult.appliedFilters,
						},
					},
				};

				console.log("Storing segments data:", {
					totalRecords: optimizedData.totalRecords,
					hasData: !!optimizedData.data,
					estimatedSize:
						Math.round(JSON.stringify(optimizedData).length / 1024) + " KB",
				});

				try {
					sessionStorage.setItem(
						"llmSegmentsSearchResults",
						JSON.stringify(optimizedData),
					);
					console.log("Segments results stored successfully in sessionStorage");
				} catch (storageError) {
					console.error(
						"SessionStorage failed, using memory storage:",
						storageError,
					);
					window.tempSegmentsResults = optimizedData;
					console.log("Stored segments results in memory instead");
				}

				setError("Segments LLM search complete! Navigating to results...");
				await new Promise((resolve) => setTimeout(resolve, 500));

				// Navigate to segments interface
				console.log("Navigating to segments results...");
				navigate("/", {
					replace: true,
					state: {
						fromLLMSearch: true,
						fromSegmentsLLMSearch: true,
						loadLLMResults: true,
						searchType: "segments",
						activeTab: "segments",
						timestamp: Date.now(),
						useMemoryStorage: !sessionStorage.getItem(
							"llmSegmentsSearchResults",
						),
					},
				});

				return; // Exit early for segments since it's a complete flow
			} else if (searchType === "audience") {
				// 🎯 USE AUDIENCE LLM BRIDGE SERVICE
				console.log("👥 Using Audience LLM Bridge Service...");
				setError("Loading Audience AI service...");

				try {
					// Dynamic import with proper error handling
					const { default: LlmApiBridgeService } = await import(
						"../services/LlmApiBridgeService"
					);
					apiService = LlmApiBridgeService; // Use the singleton instance
					console.log("✅ Audience LLM Bridge Service loaded:", apiService);

					// Cache the service globally for reuse
					window.LlmApiBridgeService = apiService;
				} catch (importError) {
					console.error(
						"❌ Failed to import LlmApiBridgeService:",
						importError,
					);
					throw new Error(
						"Audience AI service is not available. Please check your configuration.",
					);
				}

				searchTypeUpper = "AUDIENCE"; // Ensure correct API format
			} else if (searchType === "app") {
				// 📱 USE APP LLM SERVICE - EXISTING LOGIC
				console.log("📱 Using App LLM Service...");
				setError("Loading App AI service...");

				try {
					const { default: AppLLMApiService } = await import(
						"../services/AppLLMApiService"
					);
					apiService = AppLLMApiService;
					console.log("✅ App LLM Service loaded:", apiService);
					window.appLLMApiService = apiService;
				} catch (importError) {
					console.error("❌ Failed to import AppLLMApiService:", importError);
					throw new Error(
						"App AI service is not available. Please check your configuration.",
					);
				}

				searchTypeUpper = "APP";
			} else {
				// 🌐 USE DOMAIN LLM SERVICE - EXISTING LOGIC
				console.log("🌐 Using Domain LLM Service...");
				setError("Loading Domain AI service...");

				try {
					const { default: LLMApiService } = await import(
						"../services/LLMapiService"
					);
					apiService = LLMApiService;
					console.log("✅ Domain LLM Service loaded:", apiService);
					window.llmApiService = apiService;
				} catch (importError) {
					console.error("❌ Failed to import LLMApiService:", importError);
					throw new Error(
						"Domain AI service is not available. Please check your configuration.",
					);
				}
			}

			// Handle authentication for bridge services (packages, segments, audience)
			if (
				searchType === "packages" ||
				searchType === "segments" ||
				searchType === "audience"
			) {
				// Bridge services handle their own authentication internally
				console.log(
					`🔒 ${searchType} service will handle authentication internally...`,
				);
				setError(`${searchType} AI service is checking authentication...`);

				setError(
					`Authentication successful, processing ${searchType} with AI...`,
				);

				// Call appropriate LLM API for bridge services
				console.log(`📡 Calling ${searchType} LLM API...`);
				setError(`Step 1: AI is analyzing your ${searchType} query...`);

				if (searchType === "audience") {
					console.log("=== AUDIENCE LLM DEBUG ===");

					if (typeof apiService.searchWithNaturalLanguage !== "function") {
						throw new Error(
							"Audience natural language search method is not available in the service.",
						);
					}

					console.log("Calling audience natural language search...");
					llmResult = await apiService.searchWithNaturalLanguage(
						searchText,
						searchTypeUpper,
					);

					// CRITICAL: Debug the actual response structure
					console.log("=== AUDIENCE LLM RESULT DEBUG ===");
					console.log("llmResult exists:", !!llmResult);
					console.log("llmResult type:", typeof llmResult);
					console.log(
						"llmResult keys:",
						llmResult ? Object.keys(llmResult) : "null/undefined",
					);
					console.log("llmResult.success:", llmResult?.success);
					console.log("llmResult.data exists:", !!llmResult?.data);
					console.log("llmResult.data type:", typeof llmResult?.data);
					console.log(
						"llmResult.data length:",
						Array.isArray(llmResult?.data)
							? llmResult.data.length
							: "not array",
					);
					console.log("llmResult.totalRecords:", llmResult?.totalRecords);
					console.log("llmResult.totalResults:", llmResult?.totalResults);
					console.log(
						"Full llmResult structure:",
						JSON.stringify(llmResult, null, 2),
					);

					if (!llmResult || !llmResult.success) {
						throw new Error(
							`Audience search failed: ${llmResult?.error || "Unknown error"}`,
						);
					}

					// The bridge service should return the complete search results
					// Check what we actually received
					let audienceData, totalRecords;

					if (llmResult.data && Array.isArray(llmResult.data)) {
						// Case 1: Data is directly in llmResult.data
						audienceData = llmResult.data;
						totalRecords =
							llmResult.totalRecords ||
							llmResult.totalResults ||
							audienceData.length;
						console.log("Using llmResult.data directly");
					} else if (
						llmResult.audiences &&
						Array.isArray(llmResult.audiences)
					) {
						// Case 2: Data is in llmResult.audiences
						audienceData = llmResult.audiences;
						totalRecords =
							llmResult.totalRecords ||
							llmResult.totalResults ||
							audienceData.length;
						console.log("Using llmResult.audiences");
					} else {
						// Case 3: Data might be nested deeper
						console.error("Unexpected llmResult structure:", llmResult);
						throw new Error("Audience data not found in expected format");
					}

					console.log("Final data extracted:", {
						audienceCount: audienceData.length,
						totalRecords: totalRecords,
						firstAudience: audienceData[0]
							? Object.keys(audienceData[0])
							: "none",
					});

					const responseData = {
						success: true,
						totalRecords: totalRecords,
						audiences: audienceData,

						originalData: {
							body: llmResult,
							statusCode: 200,
							metadata: {
								originalQuery: searchText,
								searchType: searchType,
								isLLMEnhanced: true,
								isAudienceSearch: true,
								llmResponse: llmResult.metadata?.llmPayload,
								searchTime: llmResult.searchTime || 0,
							},
						},
					};

					console.log(
						"AUDIENCE LLM SEARCH SUCCESS! Got",
						responseData.audiences.length,
						"audiences",
					);

					// Try the optimized storage approach to avoid quota issues
					const optimizedData = {
						success: true,
						totalRecords: responseData.totalRecords,

						// Only store essential fields from each audience
						audiences: responseData.audiences.map((audience) => ({
							// Core identification
							audienceVector: audience.audienceVector || {},

							// Pass complete metrics instead of filtering
							audienceMetrics: audience.audienceMetrics || {},

							// Essential scores
							compositeScore: audience.compositeScore,
							score: audience.score,
						})),

						appliedFilters: llmResult.appliedFilters || null,

						originalData: {
							metadata: {
								originalQuery: searchText,
								searchType: searchType,
								isLLMEnhanced: true,
								isAudienceSearch: true,
								llmResponse: llmResult.metadata?.llmPayload,
								searchTime: llmResult.searchTime || 0,
								totalRecords: responseData.totalRecords,
								reversedFilters: llmResult.appliedFilters,
								extractedSearchTerms: llmResult.metadata?.extractedSearchTerms,
							},
						},
					};

					console.log("Storing optimized audience data:", {
						audienceCount: optimizedData.audiences.length,
						estimatedSize:
							Math.round(JSON.stringify(optimizedData).length / 1024) + " KB",
					});

					try {
						sessionStorage.setItem(
							"llmAudienceSearchResults",
							JSON.stringify(optimizedData),
						);
						console.log(
							"Audience results stored successfully in sessionStorage",
						);
					} catch (storageError) {
						console.error(
							"SessionStorage failed, using memory storage:",
							storageError,
						);

						// Fallback to memory storage
						window.tempAudienceResults = optimizedData;
						console.log("Stored audience results in memory instead");
					}

					setError("Audience LLM search complete! Navigating to results...");
					await new Promise((resolve) => setTimeout(resolve, 500));

					// Navigate to audience interface
					console.log("Navigating to audience results...");
					navigate("/", {
						replace: true,
						state: {
							fromLLMSearch: true,
							fromAudienceLLMSearch: true,
							loadLLMResults: true,
							searchType: "audience",
							activeTab: "audience",
							timestamp: Date.now(),
							useMemoryStorage: !sessionStorage.getItem(
								"llmAudienceSearchResults",
							),
						},
					});

					return; // Exit early for audience since it's a complete flow
				}
			} else {
				// 🌐 DOMAIN SERVICE - ALREADY HANDLED ABOVE
			}

			// ✅ AUDIENCE AND APPS/DOMAINS USE DIFFERENT AUTH PATTERNS
			if (searchType === "audience") {
				// Audience service handles its own authentication internally
				console.log(
					"🔒 Audience service will handle authentication internally...",
				);
				setError("Audience AI service is checking authentication...");

				// The LlmApiBridgeService will handle auth checks internally
				// No need for manual auth setup
			} else {
				// ✅ EXISTING AUTH LOGIC FOR APPS AND DOMAINS
				console.log("🔒 Setting up authentication...");
				setError("Checking authentication...");

				if (typeof apiService.isAuthenticated !== "function") {
					console.error(
						"❌ Service does not have isAuthenticated method:",
						apiService,
					);
					throw new Error(
						`${searchType} AI service is not properly initialized. Please refresh the page.`,
					);
				}

				if (!apiService.isAuthenticated()) {
					console.log(
						"🔄 Service not authenticated, checking for existing tokens...",
					);

					if (window.apiService?.tokens?.idToken) {
						console.log("🔄 Syncing auth token from main apiService...");
						setError("Syncing authentication...");

						if (typeof apiService.storeToken === "function") {
							const tokenSyncSuccess = apiService.storeToken(
								window.apiService.tokens.idToken,
							);

							if (!tokenSyncSuccess) {
								throw new Error(
									"Failed to sync authentication token. Please refresh the page and try again.",
								);
							}
						} else {
							console.warn(
								"⚠️ Service does not have storeToken method, trying alternative auth sync...",
							);
							if (apiService.tokens) {
								apiService.tokens.idToken = window.apiService.tokens.idToken;
							}
						}

						console.log(
							"✅ Token sync attempted, checking authentication again...",
						);
					}
				}

				if (!apiService.isAuthenticated()) {
					console.error("❌ Authentication failed after all attempts");
					throw new Error(
						"Authentication required. Please refresh the page and sign in again.",
					);
				}

				console.log("✅ Authentication verified successfully");
			}

			setError(
				`Authentication successful, processing ${searchType} with AI...`,
			);

			// ✅ CALL APPROPRIATE LLM API
			console.log(`📡 Calling ${searchType} LLM API...`);
			setError(`Step 1: AI is analyzing your ${searchType} query...`);

			if (searchType === "app") {
				// 📱 APPS: Existing logic
				if (typeof apiService.searchWithAppLLM !== "function") {
					throw new Error(
						"App LLM search method is not available in the service.",
					);
				}
				llmResult = await apiService.searchWithAppLLM(
					searchText,
					searchTypeUpper,
				);
			} else {
				// 🌐 DOMAINS: Existing logic
				if (typeof apiService.searchWithLLM !== "function") {
					throw new Error(
						"Domain LLM search method is not available in the service.",
					);
				}
				llmResult = await apiService.searchWithLLM(searchText, searchTypeUpper);
			}

			if (!llmResult || !llmResult.success) {
				throw new Error(
					`${searchType} LLM API failed: ${llmResult?.error || "Unknown error"}`,
				);
			}

			const llmData = llmResult.data;
			console.log(`✅ Step 1 Complete - ${searchType} LLM Response:`, llmData);

			// ✅ STEP 2: Call appropriate Data API (for apps and domains only)
			console.log(
				`🔍 Step 2: Searching ${searchType} database with AI insights...`,
			);
			setError(`Step 2: Searching ${searchType} database with AI insights...`);

			let dataResult;
			if (searchType === "app") {
				if (typeof apiService.performAppSearch !== "function") {
					throw new Error(
						"App data search method is not available in the service.",
					);
				}
				dataResult = await apiService.performAppSearch(llmData);
			} else {
				if (typeof apiService.performDomainSearch !== "function") {
					throw new Error(
						"Domain data search method is not available in the service.",
					);
				}
				dataResult = await apiService.performDomainSearch(llmData);
			}

			if (!dataResult || !dataResult.success) {
				throw new Error(
					`${searchType} Data API failed: ${dataResult?.error || "Unknown error"}`,
				);
			}

			const responseData = dataResult.data;
			console.log(`✅ Step 2 Complete - ${searchType} Data:`, responseData);

			// ✅ LOG SUCCESS WITH APPROPRIATE COUNT
			if (searchType === "app") {
				console.log(
					"🎉 APP LLM SEARCH SUCCESS! Got",
					responseData?.apps?.length || 0,
					"app",
				);
			} else {
				console.log(
					"🎉 DOMAIN LLM SEARCH SUCCESS! Got",
					responseData?.domains?.length || 0,
					"domains",
				);
			}

			// ✅ STORE RESULTS IN APPROPRIATE FORMAT
			let searchResultsForMainApp;

			if (searchType === "app") {
				searchResultsForMainApp = {
					success: true,
					totalRecords: responseData.totalRecords || 0,
					apps: responseData.apps || [],
					keywords: responseData.keywords || [],
					cumulativeData: responseData.cumulativeData || [],
					categories: responseData.categories || [],
					publishers: responseData.publishers || [],

					appliedFilters: responseData.appliedFilters || {},

					originalData: {
						body: responseData.body,
						statusCode: 200,
						metadata: {
							originalQuery: searchText,
							searchType: searchType,
							isLLMEnhanced: true,
							isAppSearch: true,
							llmResponse: llmData,
							originalLLMPayload: responseData.originalLLMPayload,
						},
					},
				};

				safelyStoreInSessionStorage(
					"llmAppSearchResults",
					searchResultsForMainApp,
					"App LLM results",
				);
			} else {
				searchResultsForMainApp = {
					success: true,
					totalRecords: responseData.totalRecords || 0,
					domains: responseData.domains || [],
					keywords: responseData.keywords || [],
					cumulativeData: responseData.cumulativeData || [],

					appliedFilters: responseData.appliedFilters || {},

					originalData: {
						body: responseData.body,
						statusCode: 200,
						metadata: {
							originalQuery: searchText,
							searchType: searchType,
							isLLMEnhanced: true,
							llmResponse: llmData,
							originalLLMPayload: responseData.originalLLMPayload,
						},
					},
				};

				safelyStoreInSessionStorage(
					"llmSearchResults",
					searchResultsForMainApp,
					"Domain LLM results",
				);
			}

			setError(
				`✅ ${searchType} LLM search complete! Navigating to results...`,
			);
			await new Promise((resolve) => setTimeout(resolve, 500));

			// ✅ NAVIGATE TO APPROPRIATE INTERFACE
			if (searchType === "app") {
				console.log("📱 Navigating to app results on main interface...");
				navigate("/", {
					replace: true,
					state: {
						fromLLMSearch: true,
						fromAppLLMSearch: true,
						loadLLMResults: true,
						searchType: "app",
						activeTab: "app",
						timestamp: Date.now(),
					},
				});
			} else {
				navigate("/", {
					replace: true,
					state: {
						fromLLMSearch: true,
						loadLLMResults: true,
						searchType: "domain",
					},
				});
			}
		} catch (error) {
			console.error(`❌ ${searchType} LLM search failed:`, error);
			setError(`${searchType} LLM search failed: ${error.message}`);
			setIsLoading(false);
		}
	};

	const handleNavigateToManualSearch = () => {
		if (searchType === "packages") {
			navigate("/", {
				state: {
					fromLLMSearch: true,
					switchToManual: true,
					activeTab: "packages",
				},
			});
		} else if (searchType === "audience") {
			navigate("/", {
				state: {
					fromLLMSearch: true,
					switchToManual: true,
					activeTab: "audience",
				},
			});
		} else if (searchType === "segments") {
			navigate("/", {
				state: {
					fromLLMSearch: true,
					switchToManual: true,
					activeTab: "segments",
				},
			});
		} else if (searchType === "app") {
			navigate("/", {
				state: {
					fromLLMSearch: true,
					switchToManual: true,
					activeTab: "app",
				},
			});
		} else {
			navigate("/", {
				state: {
					fromLLMSearch: true,
					switchToManual: true,
				},
			});
		}
	};

	const handleLogout = async () => {
		try {
			// Clear all services if available
			if (
				window.llmApiService &&
				typeof window.llmApiService.signOut === "function"
			) {
				window.llmApiService.signOut();
			}
			if (
				window.appLLMApiService &&
				typeof window.appLLMApiService.signOut === "function"
			) {
				window.appLLMApiService.signOut();
			}
			if (
				window.LlmApiBridgeService &&
				typeof window.LlmApiBridgeService.signOut === "function"
			) {
				window.LlmApiBridgeService.signOut();
			}
			if (
				window.segmentsLlmBridgeService &&
				typeof window.segmentsLlmBridgeService.signOut === "function"
			) {
				window.segmentsLlmBridgeService.signOut();
			}
			if (
				window.packagesLlmBridgeService &&
				typeof window.packagesLlmBridgeService.signOut === "function"
			) {
				window.packagesLlmBridgeService.signOut();
			}

			// Try dynamic imports if services aren't cached
			try {
				const { default: llmApiService } = await import(
					"../services/LLMapiService"
				);
				if (typeof llmApiService.signOut === "function") {
					llmApiService.signOut();
				}
			} catch (error) {
				console.log("LLM service not available for logout");
			}

			try {
				const { default: appLLMApiService } = await import(
					"../services/AppLLMApiService"
				);
				if (typeof appLLMApiService.signOut === "function") {
					appLLMApiService.signOut();
				}
			} catch (error) {
				console.log("App LLM service not available for logout");
			}

			try {
				const { default: LlmApiBridgeService } = await import(
					"../services/LlmApiBridgeService"
				);
				if (typeof LlmApiBridgeService.signOut === "function") {
					LlmApiBridgeService.signOut();
				}
			} catch (error) {
				console.log("Audience LLM Bridge service not available for logout");
			}

			try {
				const { default: SegmentsLlmBridgeService } = await import(
					"../services/segmentsLlmBridgeService"
				);
				if (typeof SegmentsLlmBridgeService.signOut === "function") {
					SegmentsLlmBridgeService.signOut();
				}
			} catch (error) {
				console.log("Segments LLM Bridge service not available for logout");
			}

			try {
				const { default: PackagesLlmBridgeService } = await import(
					"../services/packagesLlmBridgeService"
				);
				if (typeof PackagesLlmBridgeService.signOut === "function") {
					PackagesLlmBridgeService.signOut();
				}
			} catch (error) {
				console.log("Packages LLM Bridge service not available for logout");
			}
		} catch (error) {
			console.log("Error during service cleanup:", error);
		}

		onLogout();
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			performLLMSearch();
		}
	};

	const getPlaceholder = () => {
		switch (searchType) {
			case "packages":
				return "e.g., 'JavaScript libraries for data visualization and charts'";
			case "segments":
				return "e.g., 'millennials interested in fitness and healthy eating'";
			case "audience":
				return "e.g., 'male small business owners 40-55 Texas digital marketing'";
			case "app":
				return "e.g., 'financial news apps for mobile users in English'";
			default:
				return "e.g., 'wellness content for mobile users in Europe'";
		}
	};

	const SelectedIcon =
		searchTypes.find((type) => type.value === searchType)?.icon || Search;

	return (
		<div className="app-container">
			{/* Header using standard DomainSearchApp styling */}
			<div className="header">
				<div className="header-content">
					<div>
						<h1 className="header-title">AI-Powered Search</h1>
						<p className="header-subtitle">
							Let AI discover the perfect inventory for your campaign
						</p>
					</div>
					{/* User Menu */}
					<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
						{user && (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "12px",
									background: "rgba(255, 255, 255, 0.1)",
									borderRadius: "8px",
									padding: "8px 16px",
									color: "white",
									fontSize: "14px",
									fontWeight: "500",
								}}
							>
								<User size={20} />
								<span>{user.name || user.email}</span>
							</div>
						)}
						<button
							onClick={handleLogout}
							className="btn btn-secondary btn-small"
							style={{
								background: "rgba(255, 255, 255, 0.2)",
								color: "white",
								border: "none",
								backdropFilter: "blur(4px)",
								padding: "8px 16px",
								borderRadius: "8px",
								cursor: "pointer",
								transition: "all 0.2s",
								display: "flex",
								alignItems: "center",
								gap: "8px",
								fontSize: "14px",
								fontWeight: "500",
							}}
						>
							<LogOut size={16} />
							<span>Sign Out</span>
						</button>
					</div>
				</div>
			</div>

			{/* Main Content - Focused Layout */}
			<div className="main-layout">
				<div className="main-content">
					<div
						style={{
							width: "100%",
							maxWidth: "1100px",
							margin: "60px auto 0",
							padding: "0 24px",
							textAlign: "center",
						}}
					>
						{/* Focused Header */}
						<div style={{ marginBottom: "48px" }}>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: "12px",
									background:
										"linear-gradient(135deg,rgb(95, 167, 226) 0%,rgb(119, 193, 223) 100%)",
									padding: "12px 24px",
									borderRadius: "50px",
									color: "white",
									fontSize: "14px",
									fontWeight: "600",
									marginBottom: "24px",
									boxShadow: "0 4px 20px rgba(207, 220, 224, 0.3)",
								}}
							>
								<Sparkles size={18} />
								AI-Powered Discovery
							</div>

							<p
								style={{
									fontSize: "1.125rem",
									color: "#64748b",
									lineHeight: "1.6",
									maxWidth: "600px",
									margin: "0 auto",
								}}
							>
								Use natural language to search across domains, apps, packages,
								audience and segments — faster, smarter, targeted.
							</p>
						</div>

						{/* Error Message */}
						{error && (
							<div
								style={{
									background: error.includes("✅")
										? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
										: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
									color: "white",
									padding: "16px 24px",
									borderRadius: "12px",
									marginBottom: "32px",
									fontSize: "14px",
									fontWeight: "500",
									boxShadow: "0 4px 20px rgba(167, 147, 147, 0.1)",
									textAlign: "left",
								}}
							>
								{error}
							</div>
						)}

						{/* Enhanced Search Form */}

						<div
							style={{
								background: "white",
								border: "2px solid #e2e8f0",
								borderRadius: "20px",
								padding: "28px",
								marginBottom: "40px",
								width: "100%", // take full horizontal space
								marginLeft: "0", // remove auto centering
								marginRight: "0",
								boxShadow: "0 12px 50px rgba(0, 0, 0, 0.1)",
								transition: "all 0.3s ease",
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "stretch",
									gap: "16px",
								}}
							>
								{/* Dropdown */}
								<div style={{ position: "relative" }}>
									<button
										type="button"
										onClick={() => setIsDropdownOpen(!isDropdownOpen)}
										style={{
											display: "flex",
											alignItems: "center",
											gap: "10px",
											padding: "20px 24px",
											background: "#f8fafc",
											border: "1px solid #e2e8f0",
											borderRadius: "12px",
											fontSize: "16px",
											fontWeight: "500",
											color: "#475569",
											cursor: "pointer",
											transition: "all 0.2s",
											whiteSpace: "nowrap",
											minWidth: "150px",
										}}
									>
										<SelectedIcon size={18} />
										<span>
											{
												searchTypes.find((type) => type.value === searchType)
													?.label
											}
										</span>
										<ChevronDown
											size={16}
											style={{
												transform: isDropdownOpen
													? "rotate(180deg)"
													: "rotate(0deg)",
												transition: "transform 0.2s",
											}}
										/>
									</button>

									{isDropdownOpen && (
										<div
											style={{
												position: "absolute",
												top: "100%",
												left: "0",
												zIndex: 50,
												background: "white",
												border: "1px solid #e2e8f0",
												borderRadius: "12px",
												boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
												marginTop: "8px",
												minWidth: "180px",
												overflow: "hidden",
											}}
										>
											{searchTypes.map((type, index) => {
												const IconComponent = type.icon;
												return (
													<button
														key={type.value}
														type="button"
														onClick={() => {
															setSearchType(type.value);
															setIsDropdownOpen(false);
														}}
														style={{
															display: "flex",
															alignItems: "center",
															gap: "12px",
															width: "100%",
															padding: "14px 18px",
															background:
																searchType === type.value
																	? "#f1f5f9"
																	: "transparent",
															border: "none",
															textAlign: "left",
															fontSize: "15px",
															color: "#374151",
															cursor: "pointer",
															transition: "background 0.15s",
															borderTop:
																index > 0 ? "1px solid #f1f5f9" : "none",
														}}
														onMouseEnter={(e) =>
															(e.target.style.background = "#f8fafc")
														}
														onMouseLeave={(e) =>
															(e.target.style.background =
																searchType === type.value
																	? "#f1f5f9"
																	: "transparent")
														}
													>
														<IconComponent
															size={18}
															style={{ color: "#667eea" }}
														/>
														<span>{type.label}</span>
													</button>
												);
											})}
										</div>
									)}
								</div>

							{/* Category sub-tab: Domains / Apps */}
							{searchType === "category" && (
								<div style={{ display: "flex", flexDirection: "column", gap: 4, justifyContent: "center" }}>
									<span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
										Search in
									</span>
									<div style={{ display: "flex", gap: 4, background: "#f1f5f9", borderRadius: 8, padding: 3 }}>
										{["domains", "apps"].map((t) => (
											<button
												key={t}
												type="button"
												onClick={() => setCategorySubTab(t)}
												style={{
													padding: "6px 14px",
													border: "none",
													borderRadius: 6,
													fontSize: 13,
													fontWeight: 500,
													cursor: "pointer",
													background: categorySubTab === t ? "#fff" : "transparent",
													color: categorySubTab === t ? "#1d4ed8" : "#64748b",
													boxShadow: categorySubTab === t ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
													transition: "all 0.15s",
													whiteSpace: "nowrap",
												}}
											>
												{t === "domains" ? "Domains" : "Apps"}
											</button>
										))}
									</div>
								</div>
							)}

								{/* Search Input with dynamic placeholder */}
								<div
									style={{
										flex: 1,
										position: "relative",
										display: "flex",
										alignItems: "center",
									}}
								>
									<Brain
										size={24}
										style={{
											position: "absolute",
											left: "18px",
											top: "22px",
											color: "#94a3b8",
											zIndex: 1,
										}}
									/>

									<textarea
										value={searchText}
										onChange={(e) => {
											setSearchText(e.target.value);

											// Auto-grow logic
											e.target.style.height = "auto"; // reset height
											e.target.style.height = e.target.scrollHeight + "px"; // set to content height
										}}
										onKeyPress={handleKeyPress}
										placeholder={getPlaceholder()}
										rows={1} // start with one line
										style={{
											width: "100%",
											minHeight: "56px",
											maxHeight: "200px", // optional, prevent it from growing too much
											padding: "20px 16px 20px 54px",
											border: "1px solid #e2e8f0",
											borderRadius: "12px",
											fontSize: "16px",
											fontFamily: "inherit",
											lineHeight: "1.6",
											background: "white",
											outline: "none",
											resize: "none", // disable manual drag resize
											overflow: "hidden", // hide scrollbar
											transition: "all 0.2s ease",
										}}
										disabled={isLoading}
										onFocus={(e) => (e.target.style.borderColor = "#94a3b8")}
										onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
									/>
								</div>

								{/* Search Button */}
								<button
									type="button"
									onClick={performLLMSearch}
									disabled={isLoading || !searchText.trim()}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										width: "60px",
										height: "60px",
										background:
											isLoading || !searchText.trim()
												? "#94a3b8"
												: "linear-gradient(135deg,rgb(132, 186, 221) 0%,rgb(82, 177, 206) 100%)",
										color: "white",
										border: "none",
										borderRadius: "50%",
										cursor:
											isLoading || !searchText.trim()
												? "not-allowed"
												: "pointer",
										transition: "all 0.2s",
										boxShadow:
											isLoading || !searchText.trim()
												? "none"
												: "0 4px 20px rgba(92, 207, 236, 0.4)",
									}}
									onMouseEnter={(e) => {
										if (!isLoading && searchText.trim()) {
											e.target.style.transform = "translateY(-2px)";
											e.target.style.boxShadow =
												"0 6px 25px rgba(100, 192, 235, 0.5)";
										}
									}}
									onMouseLeave={(e) => {
										e.target.style.transform = "translateY(0px)";
										e.target.style.boxShadow =
											isLoading || !searchText.trim()
												? "none"
												: "0 4px 20px rgba(85, 171, 228, 0.4)";
									}}
								>
									{isLoading ? (
										<div
											style={{
												width: "20px",
												height: "20px",
												border: "2px solid rgba(255, 255, 255, 0.3)",
												borderTop: "2px solid white",
												borderRadius: "50%",
												animation: "spin 1s linear infinite",
											}}
										></div>
									) : (
										<ChevronUp size={28} />
									)}
								</button>
							</div>
						</div>
					</div>

					{/* AI Processing Indicator */}
					{isLoading && (
						<div
							style={{
								background:
									"linear-gradient(135deg, #667eea 0%,rgb(75, 130, 162) 100%)",
								color: "white",
								padding: "20px 24px",
								borderRadius: "12px",
								marginBottom: "32px",
								textAlign: "left",
								boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "12px",
									marginBottom: "8px",
								}}
							>
								<div
									style={{
										width: "20px",
										height: "20px",
										border: "2px solid rgba(255, 255, 255, 0.3)",
										borderTop: "2px solid white",
										borderRadius: "50%",
										animation: "spin 1s linear infinite",
									}}
								></div>
								<span style={{ fontWeight: "600" }}>
									AI is analyzing your query and discovering relevant
									inventory...
								</span>
							</div>
							<p
								style={{
									margin: 0,
									opacity: 0.9,
									fontSize: "14px",
								}}
							>
								Processing natural language, applying smart filters, and
								searching our database.
							</p>
						</div>
					)}

					{/* Quick Access */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "16px",
							color: "#64748b",
							fontSize: "14px",
						}}
					>
						<span>Prefer manual search with detailed filters?</span>
						<button
							onClick={handleNavigateToManualSearch}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								padding: "8px 16px",
								background: "transparent",
								color: "#1E90FF",
								border: "1px solid #1E90FF",
								borderRadius: "8px",
								fontSize: "14px",
								fontWeight: "500",
								cursor: "pointer",
								transition: "all 0.2s",
							}}
							onMouseEnter={(e) => {
								e.target.style.background = "#1E90FF";
								e.target.style.color = "white";
							}}
							onMouseLeave={(e) => {
								e.target.style.background = "transparent";
								e.target.style.color = "#1E90FF";
							}}
						>
							<span>Manual Search</span>
							<ArrowRight size={14} />
						</button>
					</div>
				</div>
			</div>

			{/* Add spinning animation */}
			<style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
		</div>
	);
};

export default SearchPage;
